import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api, ApiError } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import type {
  CartDto,
  CartMutationResult,
  CheckoutStartResult,
  OrderConfirmationDto,
  SlotConflictBody,
} from '@/api/types'

/**
 * The current user's cart. Every mutation endpoint that returns a cart replaces `cart` wholesale
 * (the server is the source of truth for totals/conflicts); mutations that return a result object
 * (add / process-wishlist) are followed by a reload. `count` feeds the subnav badge — before the
 * cart is first loaded it falls back to MeResponse.cartCount so the badge is right on first paint
 * without an extra request.
 */
export const useCartStore = defineStore('cart', () => {
  const cart = ref<CartDto | null>(null)
  const loaded = ref(false)
  const loading = ref(false)

  /** The order confirmation from the most recent zero-sum checkout (read by /cart/confirmation). */
  const lastConfirmation = ref<OrderConfirmationDto | null>(null)

  const count = computed(() => {
    if (cart.value) return cart.value.itemCount
    const auth = useAuthStore()
    return auth.user?.cartCount ?? 0
  })

  async function load(): Promise<void> {
    loading.value = true
    try {
      cart.value = await api.get<CartDto>('/api/v1/cart')
      loaded.value = true
    } finally {
      loading.value = false
    }
  }

  /**
   * Synchronous add. Resolves with the full multi-bucket result (200) and refreshes the cart.
   * A 409 SLOT_CONFLICT is rethrown as-is (ApiError with a SlotConflictBody body) — callers
   * render the inline conflict message; use `slotConflictOf` to extract it.
   */
  async function addEvent(
    eventID: number,
    quantity?: number,
    forUserID?: number,
  ): Promise<CartMutationResult> {
    const result = await api.post<CartMutationResult>('/api/v1/cart/events', {
      eventID,
      quantity,
      forUserID,
    })
    await load()
    return result
  }

  /** Remove a cart line; the endpoint returns the refreshed cart. */
  async function removeEvent(regEventID: number): Promise<void> {
    cart.value = await api.del<CartDto>(`/api/v1/cart/events/${regEventID}`)
    loaded.value = true
  }

  /** Remove a purchasing badge registration (wave-2 endpoint); returns the refreshed cart. */
  async function removeRegistration(registrationID: number): Promise<void> {
    cart.value = await api.del<CartDto>(`/api/v1/cart/registration/${registrationID}`)
    loaded.value = true
  }

  /** Process the user's wishlist into the cart; refreshes the cart, returns per-item outcomes. */
  async function processWishlist(): Promise<CartMutationResult> {
    const result = await api.post<CartMutationResult>('/api/v1/cart/wishlist')
    await load()
    return result
  }

  /** Start a PayPal checkout — the caller redirects the browser to `approvalUrl`. */
  function checkoutPaypal(): Promise<CheckoutStartResult> {
    return api.post<CheckoutStartResult>('/api/v1/cart/checkout/paypal')
  }

  /** Execute the approved PayPal payment (the /cart/confirmation return leg). */
  async function checkoutExecute(paymentId: string, payerId: string): Promise<OrderConfirmationDto> {
    const confirmation = await api.post<OrderConfirmationDto>('/api/v1/cart/checkout/execute', {
      paymentId,
      payerId,
    })
    lastConfirmation.value = confirmation
    await load().catch(() => undefined)
    return confirmation
  }

  /** Zero-sum checkout — approves the cart without a payment provider when the total is $0. */
  async function checkoutZero(): Promise<OrderConfirmationDto> {
    const confirmation = await api.post<OrderConfirmationDto>('/api/v1/cart/checkout/zero')
    lastConfirmation.value = confirmation
    await load().catch(() => undefined)
    return confirmation
  }

  function reset(): void {
    cart.value = null
    loaded.value = false
    lastConfirmation.value = null
  }

  return {
    cart,
    loaded,
    loading,
    count,
    lastConfirmation,
    load,
    addEvent,
    removeEvent,
    removeRegistration,
    processWishlist,
    checkoutPaypal,
    checkoutExecute,
    checkoutZero,
    reset,
  }
})

/** Extract the 409 SLOT_CONFLICT body from an error thrown by addEvent, or null. */
export function slotConflictOf(e: unknown): SlotConflictBody | null {
  if (e instanceof ApiError && e.status === 409) {
    const body = e.body as Partial<SlotConflictBody> | undefined
    if (body && body.code === 'SLOT_CONFLICT') return body as SlotConflictBody
  }
  return null
}
